import { Platform } from 'react-native';
import type { User } from '../types/auth';
import { getCachedRegion } from '../services/region';

/**
 * AMB-224 — central rule for region-restricted UI gating.
 *
 * Currently scoped to "is this user in RU?" — when true:
 *   - the profile editor hides the Instagram input;
 *   - public-profile / guest views drop the Instagram link from the
 *     social-links row;
 *   - the global ShareModal drops the Facebook tile.
 *
 * The BE GeoIP service is the source of truth (`user.region` from
 * `/api/auth/me`, or the anonymous `/api/region` endpoint). When we
 * don't have a server-resolved value yet — initial render, anonymous
 * viewer before the region call lands, dev environments where the
 * GeoIP DB is missing — we fall back to the browser timezone, which
 * is good enough to keep restricted UI hidden from the obvious RU
 * audience without forcing a network round-trip on first paint.
 *
 * Returning `false` is the safe default for any signal we can't
 * interpret — we only hide UI when at least one signal positively
 * indicates RU. The opposite default (hide on uncertainty) would
 * silently break the experience for everyone whenever GeoIP is
 * misconfigured.
 */

/**
 * IANA timezones that map to Russia.
 *
 * Sources:
 *   - tz database "country_code" mapping for "RU"
 *     (https://www.iana.org/time-zones).
 *   - Linked legacy names that older browsers still report.
 *
 * Keeping these as a plain array (instead of a Set) so it's easy to
 * spot-check on review.
 */
const RU_TIMEZONES: ReadonlyArray<string> = [
  'Europe/Moscow',
  'Europe/Kaliningrad',
  'Europe/Samara',
  'Europe/Saratov',
  'Europe/Volgograd',
  'Europe/Astrakhan',
  'Europe/Ulyanovsk',
  'Europe/Kirov',
  'Asia/Yekaterinburg',
  'Asia/Omsk',
  'Asia/Novosibirsk',
  'Asia/Novokuznetsk',
  'Asia/Krasnoyarsk',
  'Asia/Barnaul',
  'Asia/Tomsk',
  'Asia/Irkutsk',
  'Asia/Chita',
  'Asia/Yakutsk',
  'Asia/Khandyga',
  'Asia/Vladivostok',
  'Asia/Ust-Nera',
  'Asia/Magadan',
  'Asia/Sakhalin',
  'Asia/Srednekolymsk',
  'Asia/Kamchatka',
  'Asia/Anadyr',
];

/**
 * Read the browser/native timezone. Wrapped so callers don't have to
 * worry about `Intl` being unavailable (rare, but possible in some
 * RN Hermes builds and in SSR-y contexts).
 */
const getBrowserTimezone = (): string | null => {
  try {
    // Available in both web and modern React Native.
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
};

/**
 * Map a timezone string to its ISO 3166-1 country code if recognised.
 * Returns null for unknown timezones — that's "no signal", not "non-RU".
 */
export const countryFromTimezone = (tz: string | null | undefined): string | null => {
  if (!tz) return null;
  return RU_TIMEZONES.includes(tz) ? 'RU' : null;
};

export type RegionInput = {
  /** ISO country code from the BE GeoIP service. */
  region?: string | null;
  /** IANA timezone from the user profile (AppUser.Timezone). */
  timezone?: string | null;
};

/**
 * Returns true when the caller should hide region-restricted UI for
 * this user (currently RU only). Pass the user object directly — both
 * `region` and `timezone` are read.
 *

 * For anonymous viewers, the helper transparently consults the
 * 24h-cached `/api/region` response via `services/region.ts`. Call
 * `getUserRegion()` once on app mount to populate that cache; from
 * then on `isRestrictedRegion(null)` returns the right answer
 * synchronously.
 */
export const isRestrictedRegion = (input: RegionInput | User | null | undefined): boolean => {
  if (!input) {
    // Anonymous viewer path — prefer the cached BE answer over the
    // timezone heuristic so a Russian-speaking expat browsing from
    // abroad isn't incorrectly gated.
    const cached = getCachedRegion();
    if (cached === 'RU') return true;
    if (cached && cached !== 'RU') return false;
    // No cache yet (first paint, or cache cleared) → timezone fallback.
    return countryFromTimezone(getBrowserTimezone()) === 'RU';
  }
  if (input.region === 'RU') return true;
  // Authoritative non-RU answer from the server — trust it and skip
  // the timezone fallback. Otherwise a Russian-speaking expat with
  // their laptop still set to Europe/Moscow would be treated as RU.
  if (input.region && input.region !== 'RU') return false;
  // Server didn't resolve (region === null/undefined). Fall back to
  // the user's stored timezone, then to the browser timezone.
  if (countryFromTimezone(input.timezone) === 'RU') return true;
  if (Platform.OS === 'web') {
    return countryFromTimezone(getBrowserTimezone()) === 'RU';
  }
  return false;
};
